/**
 * Utilitaires pour la gestion des permissions côté frontend
 */

/**
 * Vérifier si l'utilisateur a une permission spécifique
 * @param {Object} user - Objet utilisateur avec roles et permissions
 * @param {string} permission - Permission à vérifier (ex: 'payments.create')
 * @returns {boolean}
 */
export const hasPermission = (user, permission) => {
  if (!user || !user.roles) return false;
  
  // Récupérer toutes les permissions de l'utilisateur
  const allPermissions = getAllUserPermissions(user);
  return allPermissions.includes(permission);
};

/**
 * Vérifier si l'utilisateur a un rôle spécifique
 * @param {Object} user - Objet utilisateur
 * @param {string} roleSlug - Slug du rôle à vérifier
 * @returns {boolean}
 */
export const hasRole = (user, roleSlug) => {
  if (!user || !user.roles) return false;
  return user.roles.some(role => role.slug === roleSlug);
};

/**
 * Vérifier si l'utilisateur peut effectuer une action sur un module
 * @param {Object} user - Objet utilisateur
 * @param {string} module - Module (ex: 'payments')
 * @param {string} action - Action (ex: 'create')
 * @returns {boolean}
 */
export const canPerformAction = (user, module, action) => {
  return hasPermission(user, `${module}.${action}`);
};

/**
 * Obtenir toutes les permissions de l'utilisateur
 * @param {Object} user - Objet utilisateur
 * @returns {string[]} Array des noms de permissions
 */
export const getAllUserPermissions = (user) => {
  if (!user) return [];
  
  const permissions = new Set();
  
  // Permissions des rôles
  if (user.roles) {
    user.roles.forEach(role => {
      if (role.permissions) {
        role.permissions.forEach(permission => {
          permissions.add(permission.name);
        });
      }
    });
  }
  
  // Permissions individuelles
  if (user.additionalPermissions) {
    user.additionalPermissions.forEach(permission => {
      permissions.add(permission.name);
    });
  }
  
  return Array.from(permissions);
};

/**
 * Obtenir les permissions par module
 * @param {Object} user - Objet utilisateur
 * @param {string} module - Module à filtrer
 * @returns {string[]} Array des permissions pour le module
 */
export const getPermissionsByModule = (user, module) => {
  const allPermissions = getAllUserPermissions(user);
  return allPermissions.filter(permission => permission.startsWith(`${module}.`));
};

/**
 * Vérifier si l'utilisateur est administrateur
 * @param {Object} user - Objet utilisateur
 * @returns {boolean}
 */
export const isAdmin = (user) => {
  return hasRole(user, 'administrateur');
};

/**
 * Vérifier si l'utilisateur est comptable
 * @param {Object} user - Objet utilisateur
 * @returns {boolean}
 */
export const isComptable = (user) => {
  return hasRole(user, 'comptable');
};

/**
 * Vérifier si l'utilisateur est gestionnaire RH
 * @param {Object} user - Objet utilisateur
 * @returns {boolean}
 */
export const isGestionnaireRH = (user) => {
  return hasRole(user, 'gestionnaire-rh');
};

/**
 * Vérifier si l'utilisateur est gestionnaire pédagogique
 * @param {Object} user - Objet utilisateur
 * @returns {boolean}
 */
export const isGestionnairePedagogique = (user) => {
  return hasRole(user, 'gestionnaire-pedagogique');
};

/**
 * Vérifier si l'utilisateur est superviseur
 * @param {Object} user - Objet utilisateur
 * @returns {boolean}
 */
export const isSuperviseur = (user) => {
  return hasRole(user, 'superviseur');
};

/**
 * Obtenir les modules accessibles pour un utilisateur
 * @param {Object} user - Objet utilisateur
 * @returns {Object} Objet avec les modules et leurs actions disponibles
 */
export const getAccessibleModules = (user) => {
  const permissions = getAllUserPermissions(user);
  const modules = {};
  
  permissions.forEach(permission => {
    const [module, action] = permission.split('.');
    if (!modules[module]) {
      modules[module] = [];
    }
    modules[module].push(action);
  });
  
  return modules;
};

/**
 * Générer le menu de navigation basé sur les permissions
 * @param {Object} user - Objet utilisateur
 * @returns {Array} Array des éléments de menu autorisés
 */
export const getAuthorizedMenuItems = (user) => {
  const menuItems = [];
  
  // Dashboard - accessible à tous
  menuItems.push({
    name: 'Dashboard',
    path: '/dashboard',
    icon: 'HomeIcon',
    always: true
  });
  
  // Gestion financière
  if (hasPermission(user, 'payments.read')) {
    menuItems.push({
      name: 'Paiements',
      path: '/payments',
      icon: 'CurrencyDollarIcon',
      module: 'payments'
    });
  }
  
  // Ressources humaines
  if (hasPermission(user, 'teachers.read')) {
    menuItems.push({
      name: 'Enseignants',
      path: '/teachers',
      icon: 'UserGroupIcon',
      module: 'teachers'
    });
  }
  
  if (hasPermission(user, 'schedules.read')) {
    menuItems.push({
      name: 'Emplois du temps',
      path: '/schedules',
      icon: 'CalendarIcon',
      module: 'schedules'
    });
  }
  
  // Gestion pédagogique
  if (hasPermission(user, 'students.read')) {
    menuItems.push({
      name: 'Élèves',
      path: '/students',
      icon: 'UserIcon',
      module: 'students'
    });
  }
  
  if (hasPermission(user, 'classes.read')) {
    menuItems.push({
      name: 'Classes',
      path: '/classes',
      icon: 'BuildingLibraryIcon',
      module: 'classes'
    });
  }
  
  if (hasPermission(user, 'grades.read')) {
    menuItems.push({
      name: 'Notes',
      path: '/grades',
      icon: 'DocumentTextIcon',
      module: 'grades'
    });
  }
  
  if (hasPermission(user, 'bulletins.read')) {
    menuItems.push({
      name: 'Bulletins',
      path: '/bulletins',
      icon: 'DocumentCheckIcon',
      module: 'bulletins'
    });
  }
  
  // Rapports
  if (hasPermission(user, 'reports.read')) {
    menuItems.push({
      name: 'Rapports',
      path: '/reports',
      icon: 'ChartBarIcon',
      module: 'reports'
    });
  }
  
  // Administration
  if (hasPermission(user, 'users.read') || hasPermission(user, 'administration.manage')) {
    menuItems.push({
      name: 'Administration',
      path: '/admin',
      icon: 'CogIcon',
      module: 'administration',
      children: []
    });
    
    const adminItem = menuItems[menuItems.length - 1];
    
    if (hasPermission(user, 'users.read')) {
      adminItem.children.push({
        name: 'Utilisateurs',
        path: '/admin/users',
        icon: 'UsersIcon'
      });
    }
    
    if (hasPermission(user, 'administration.manage')) {
      adminItem.children.push({
        name: 'Paramètres',
        path: '/admin/settings',
        icon: 'AdjustmentsHorizontalIcon'
      });
    }
  }
  
  return menuItems;
};

/**
 * Vérifier si l'utilisateur peut accéder à une route
 * @param {Object} user - Objet utilisateur
 * @param {string} path - Chemin de la route
 * @returns {boolean}
 */
export const canAccessRoute = (user, path) => {
  // Routes publiques
  const publicRoutes = ['/login', '/register', '/forgot-password'];
  if (publicRoutes.includes(path)) return true;
  
  // Dashboard accessible à tous les utilisateurs connectés
  if (path === '/dashboard' || path === '/') return !!user;
  
  // Mappage des routes vers les permissions
  const routePermissions = {
    '/payments': 'payments.read',
    '/payments/create': 'payments.create',
    '/teachers': 'teachers.read',
    '/teachers/create': 'teachers.create',
    '/schedules': 'schedules.read',
    '/schedules/create': 'schedules.create',
    '/students': 'students.read',
    '/students/create': 'students.create',
    '/classes': 'classes.read',
    '/classes/create': 'classes.create',
    '/grades': 'grades.read',
    '/bulletins': 'bulletins.read',
    '/reports': 'reports.read',
    '/admin': 'users.read',
    '/admin/users': 'users.read',
    '/admin/settings': 'administration.manage'
  };
  
  // Vérifier les routes exactes
  if (routePermissions[path]) {
    return hasPermission(user, routePermissions[path]);
  }
  
  // Vérifier les routes avec paramètres (ex: /students/123/edit)
  for (const [route, permission] of Object.entries(routePermissions)) {
    if (path.startsWith(route + '/')) {
      return hasPermission(user, permission);
    }
  }
  
  // Par défaut, refuser l'accès aux routes non définies
  return false;
};

/**
 * Obtenir les actions disponibles pour un module
 * @param {Object} user - Objet utilisateur
 * @param {string} module - Module à vérifier
 * @returns {Object} Objet avec les actions disponibles
 */
export const getModuleActions = (user, module) => {
  return {
    canRead: hasPermission(user, `${module}.read`),
    canCreate: hasPermission(user, `${module}.create`),
    canUpdate: hasPermission(user, `${module}.update`),
    canDelete: hasPermission(user, `${module}.delete`),
    canExport: hasPermission(user, `${module}.export`),
    canPrint: hasPermission(user, `${module}.print`),
    canManage: hasPermission(user, `${module}.manage`)
  };
};

/**
 * Constantes pour les rôles
 */
export const ROLES = {
  ADMIN: 'administrateur',
  SUPERVISEUR: 'superviseur',
  COMPTABLE: 'comptable',
  GESTIONNAIRE_RH: 'gestionnaire-rh',
  GESTIONNAIRE_PEDAGOGIQUE: 'gestionnaire-pedagogique'
};

/**
 * Constantes pour les modules
 */
export const MODULES = {
  PAYMENTS: 'payments',
  TEACHERS: 'teachers',
  SCHEDULES: 'schedules',
  STUDENTS: 'students',
  CLASSES: 'classes',
  GRADES: 'grades',
  BULLETINS: 'bulletins',
  USERS: 'users',
  REPORTS: 'reports',
  ADMINISTRATION: 'administration'
};

/**
 * Constantes pour les actions
 */
export const ACTIONS = {
  CREATE: 'create',
  READ: 'read',
  UPDATE: 'update',
  DELETE: 'delete',
  EXPORT: 'export',
  PRINT: 'print',
  MANAGE: 'manage'
};